import { io } from 'socket.io-client';
import { SOCKET_BASE, AUTH_TOKEN } from '../config';
import { useWorldStore } from '../state/worldStore';

// worldSocket — the client half of the /world namespace (see API src/sockets/world.js).
// Two jobs mirroring the server: (1) keep a live roster of PEERS in your current
// area and feed them into worldStore.presences; (2) track a server-time OFFSET so
// serverNow() agrees with everyone else (shared clock). Position emitting (emitMove)
// is called from the frame loop once wired; until then peers show without live pos.

let socket = null;
let offset = 0;                 // serverNow() = Date.now() + offset
const peers = new Map();        // socket.id -> { id, handle, avatarUrl, member, pos, heading }
let myPos = null;               // my own latest [x, z] — used to leave an avatar behind on switch
let myHeading = 0;
let storeUnsub = null;          // worldStore subscription (cleaned up on disconnect)

// Shared world clock. Corrected off each `world_state {t}` broadcast. RTT is ignored
// (a few hundred ms is irrelevant for day/night + beat).
export function serverNow() { return Date.now() + offset; }

// Which owned avatar a MEMBER appears as (avatar_id). Sent in the socket handshake;
// the server resolves that avatar's name + headshot for everyone's presence view.
const APPEAR_KEY = 'world:appearAs';
export function getAppearAs() { try { return localStorage.getItem(APPEAR_KEY) || ''; } catch { return ''; } }
export function setAppearAs(avatarId) {
  // Leaving your current avatar: drop it right where you stand (locally on YOUR
  // screen — the server persists it + hands it to other observers on disconnect),
  // then reconnect as the new one (SpawnAsAvatar warps you to it).
  // Client-authoritative "who am I leaving" (localStorage), falling back to the
  // server-resolved id — avoids the `me` event lag targeting the wrong avatar.
  const oldId = parseInt(getAppearAs(), 10) || useWorldStore.getState().myAvatarId;
  if (oldId && oldId !== avatarId && myPos) {
    useWorldStore.getState().bumpNpcPos(oldId, myPos[0], myPos[1], myHeading);
  }
  try { localStorage.setItem(APPEAR_KEY, String(avatarId || '')); } catch { /* private mode */ }
  disconnectWorld();
  connectWorld();
}

function pushPeers() {
  // `userId` mirrors `id` so the existing venue presence render (keys on userId) keeps
  // working while we transition to position-accurate rendering.
  const list = [...peers.values()].map((p) => ({ ...p, userId: p.id }));
  useWorldStore.getState().setPresences(list);
}

// The area you're in — an interior id when inside, else the coarse zoom (street /
// district / orbit). People sharing an area exchange positions.
function roomKey() {
  const s = useWorldStore.getState();
  return s.interior || (s.zoom || 'street');
}

export function connectWorld() {
  if (socket) return socket;
  const wanted = getAppearAs() || undefined;
  // WHO WE ASKED TO BE vs WHO WE CAME BACK AS. The server falls back to your
  // first owned avatar when it cannot honour `appearAs`, and it did so silently
  // — so "I picked a new artist and entered as my old one" (Khari, 2026-08-06)
  // is indistinguishable from the client never having sent the pick. These two
  // lines split that in half without a restart.
  console.info(`[world] connecting as appearAs=${wanted ?? 'NONE (server will pick your first avatar)'}`);
  socket = io(`${SOCKET_BASE}/world`, {
    auth: { token: AUTH_TOKEN || undefined, appearAs: wanted },
    transports: ['websocket'],
    reconnection: true,
  });

  socket.on('world_state', ({ t, online }) => {
    if (t) offset = t - Date.now();
    if (typeof online === 'number') useWorldStore.getState().setOnline(online);
  });

  // Which world avatars are player-driven right now → the scene hides their NPC.
  socket.on('embodied', (ids) => useWorldStore.getState().setEmbodied(Array.isArray(ids) ? ids : []));
  // Which avatar THIS member is embodying (server-resolved: chosen or first owned).
  socket.on('me', (data) => {
    const got = data?.avatarId || null;
    if (wanted && String(got) !== String(wanted)) {
      console.warn(`[world] ⚠ asked to appear as avatar ${wanted} but the server made you ${got}. `
        + 'It could not honour the pick (not owned, or no row) and fell back to your first owned avatar with an image.');
    }
    useWorldStore.getState().setMyAvatarId(got);
  });

  socket.on('roster', (list) => { peers.clear(); (list || []).forEach((p) => peers.set(p.id, p)); pushPeers(); });
  socket.on('peer_enter', (p) => { peers.set(p.id, p); pushPeers(); });
  socket.on('peer_move', ({ id, pos, heading }) => {
    const p = peers.get(id);
    if (p) { p.pos = pos; p.heading = heading; pushPeers(); }
  });
  // Proximity chat: a peer said something → bubble over their figure + chat log.
  socket.on('peer_say', (m) => {
    if (!m?.id) return;
    const p = peers.get(m.id);
    if (p) { p.say = { text: m.text, ts: m.ts }; pushPeers(); }
    useWorldStore.getState().pushChat({ id: m.id, handle: m.handle, text: m.text, ts: m.ts });
  });

  socket.on('peer_leave', ({ id }) => {
    const p = peers.get(id);
    // Hand a driven avatar's LAST live position to its NPC before it un-hides, so
    // it stays where the marker was (not the stale page-load spot) on logout.
    if (p && p.avatarId && Array.isArray(p.pos)) {
      useWorldStore.getState().bumpNpcPos(p.avatarId, p.pos[0], p.pos[2], p.heading);
    }
    if (peers.delete(id)) pushPeers();
  });
  // SOMEBODY IS CALLING YOU. Addressed to the member, not to a room — the
  // server puts every signed-in socket in a `user:<id>` channel for exactly
  // this. A notification (and push) goes out alongside; this is the copy that
  // arrives while the caller is still holding the phone to their ear.
  socket.on('incoming_call', (payload = {}) => {
    if (!payload.code) return;
    console.info(`[world] incoming call from ${payload.from?.name || 'someone'} (${payload.code})`);
    useWorldStore.getState().ringVideo({
      code: payload.code,
      from: payload.from || null,
      asAvatarId: payload.asAvatarId || null,
      at: payload.at || Date.now(),
    });
  });

  // SOMEBODY SAVED YOU A SEAT. Same channel and same reasoning as the call
  // above — addressed to the member via `user:<id>`, arriving while the host is
  // still holding a chair, with the notification as the copy you find later.
  socket.on('listening_invite', (payload = {}) => {
    if (!payload.code) return;
    console.info(`[world] listening invite from ${payload.from?.name || 'someone'} (${payload.code}, ${payload.zone})`);
    const inv = {
      code: payload.code,
      roomId: payload.roomId || null,
      zone: payload.zone || null,
      from: payload.from || null,
      at: payload.at || Date.now(),
    };
    const st = useWorldStore.getState();
    st.setListeningInvite(inv);
    // AND INTO THE PHONE. The socket message alone only reached somebody who
    // happened to be standing in the right room with the dock open — an invite
    // has to survive not looking at it. The Calls app badges, keeps it, and
    // gives it a button.
    st.noteListeningInvite(inv);
    // Bring the phone up on the Calls screen: "it should appear in my phone (on
    // screen)". Deliberately NOT a full-screen ring like a video call — you are
    // being offered a seat, not summoned, and hijacking the viewport for it
    // would make every invite an interruption.
    try { st.setPhoneApp?.('calls'); st.setPhoneOpen?.(true); } catch { /* phone not mounted */ }
  });

  // The room changed: somebody sat down, a chair freed, the host put a record
  // on. Broadcast to the ZONE, so it reaches everyone standing in it whether or
  // not they have a seat — an intimate room is partly about being able to see
  // it filling up.
  socket.on('listening_state', (payload = {}) => {
    if (payload?.room) useWorldStore.getState().setListeningRoom(payload.room);
  });

  socket.on('disconnect', () => { peers.clear(); pushPeers(); });

  // Follow the local player's area: (re)announce `enter` whenever the room changes.
  let lastRoom = null;
  const syncRoom = () => {
    if (!socket || !socket.connected) return; // may be null after disconnect
    const room = roomKey();
    if (room === lastRoom) return;
    lastRoom = room;
    peers.clear(); pushPeers();      // leaving an area clears its roster
    socket.emit('enter', { room });
  };
  socket.on('connect', () => { lastRoom = null; syncRoom(); });
  // Cheap guard inside syncRoom means subscribing to every store change is fine.
  // Keep the unsub so disconnect can drop it (else it fires against a null socket).
  if (storeUnsub) storeUnsub();
  storeUnsub = useWorldStore.subscribe(syncRoom);

  // Clean, INSTANT leave on refresh/close/tab-hide (the common case) so our marker
  // doesn't linger for others. A hard crash/kill is caught by the server's ping
  // timeout instead. pagehide fires on iOS where beforeunload doesn't.
  window.addEventListener('pagehide', () => { try { socket?.emit('leave'); } catch { /* unloading */ } });

  return socket;
}

// Called from WalkControls/InteriorWalk once position emitting is wired. Throttled
// to ~8 Hz; the server relays as volatile (drops under congestion).
let lastMove = 0;
export function emitMove(pos, heading) {
  if (Array.isArray(pos)) { myPos = [pos[0], pos[2]]; myHeading = heading || 0; } // track my own latest
  if (!socket || !socket.connected) return;
  const now = Date.now();
  if (now - lastMove < 120) return;
  lastMove = now;
  socket.emit('move', { pos, heading });
}

// Send a proximity chat line to peers in your area. Locally echoed into the log
// (you don't see your own bubble in first-person).
export function sendSay(text) {
  const t = String(text || '').trim().slice(0, 200);
  if (!socket || !socket.connected || !t) return;
  socket.emit('say', { text: t });
  useWorldStore.getState().pushChat({ id: 'me', handle: 'You', text: t, ts: Date.now(), me: true });
}

export function disconnectWorld() {
  if (storeUnsub) { storeUnsub(); storeUnsub = null; }
  if (socket) { try { socket.disconnect(); } catch { /* noop */ } socket = null; }
}
